import React, { useState, useCallback } from 'react';
import { Toast, ToastProps } from '../components/Toast';
import { ToastContext, ToastContextType } from './ToastContext';

interface ToastItem extends ToastProps {
  id: string;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: Omit<ToastProps, 'id' | 'onClose'>) => {
      const id =
        Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const newToast: ToastItem = {
        ...toast,
        id,
        onClose: removeToast,
      };
      setToasts(prev => [...prev, newToast]);
    },
    [removeToast]
  );

  const showError = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'error', title, message });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'warning', title, message });
    },
    [showToast]
  );

  const showSuccess = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'success', title, message });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (title: string, message?: string) => {
      showToast({ type: 'info', title, message });
    },
    [showToast]
  );

  const contextValue: ToastContextType = React.useMemo(() => ({
    showToast,
    showError,
    showWarning,
    showSuccess,
    showInfo,
  }), [showToast, showError, showWarning, showSuccess, showInfo]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Render toasts */}
      {toasts.map((toast, index) => (
        <div key={toast.id} style={{ zIndex: 9999 + index }}>
          <Toast {...toast} />
        </div>
      ))}
    </ToastContext.Provider>
  );
};
